import {
  Lock,
  LockOpen,
  TextFields,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useCallback, useState, useEffect, useRef, useContext } from "react";
import {
  LinkBubbleMenu,
  MenuButton,
  RichTextEditor,
  insertImages,
  TableBubbleMenu,
} from "mui-tiptap";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./useExtensions";
import { USERROLE } from "../../utils/commonConstant";
import { HighlightContext } from "../../context/HighlightContext";
import { processParaEnableDisableTrackHistory } from "../../utils/formUtils";
import HighlightManager from "./comments/HighlightManagerPreMode";

function fileListToImageFiles(fileList) {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();
    return mimeType.startsWith("image/");
  });
}

export default function Editor({
  border = false,
  menuBar = true,
  editable = true,
  text,
  onTextChange,
  elementId,
  backgroundColor,
  onEditorInlineFieldsChange,
  existingInlineFields,
  elementProps,
  comments,
  trackChangesComments,
  selectedElementId,
  formPreview,
  trackChangeDisplayBy = USERROLE.ADMIN,
  trackHistorys,
  isShowTrackHistory = false,
  isPresentMode = false,
}) {
  console.log("formPreview = " + formPreview);
  const rteRef = useRef(elementId);
  const editorContainerRef = useRef(null);
  const [isEditable, setIsEditable] = useState(editable);
  const [showMenuBar, setShowMenuBar] = useState(false);
  const [submittedContent, setSubmittedContent] = useState(text);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(formPreview);
  console.log(trackChangeDisplayBy, "trackChangeDisplayBy");
  const highlightContext = useContext(HighlightContext);
  const [isTrackingHistoryEnabled, setIsTrackingHistoryEnabled] =
    useState(isShowTrackHistory);
  const [isHighlightPluginEnabled, setIsHighlightPluginEnabled] =
    useState(formPreview);
  const handleContentChange = useCallback(
    (forceSave = false) => {
      if (onTextChange) {
        const editor = rteRef.current?.editor;
        if (!editor) return;

        const content = editor.getHTML() ?? "";
        // Extract regular comments
        const commentsMap =
          editor.storage?.selectionHighlight?.comments || new Map();
        const commentsArray = [...commentsMap];

        // Extract track changes comments from storage
        const trackChangesCommentsMap =
          editor.storage?.trackChanges?.trackChangesComments || new Map();

        // Extract track changes history from storage
        const historyMap =
          editor.storage?.trackChanges?.trackHistorys || new Map();

        onTextChange(
          content,
          commentsArray,
          trackChangesCommentsMap,
          forceSave,
          historyMap
        );
      }
    },
    [onTextChange]
  );

  const handleSave = useCallback(
    (forceSave = false) => {
      handleContentChange(forceSave);
      console.log("Save clicked: comments and content have been processed");
    },
    [handleContentChange]
  );

  const extensions = useExtensions({
    placeholder: "Add your own content here...",
    isEditable: editable,
    elementId: elementId,
    existingInlineFields: existingInlineFields || [],
    onEditorInlineFieldsChange: onEditorInlineFieldsChange,
    elementProps: elementProps,
    isBorder: border,
    comments: comments,
    trackChangesComments: trackChangesComments || new Map(),
    isTrackingEnabled,
    trackChangeDisplayBy: trackChangeDisplayBy,
    highlightContext: highlightContext,
    handleSave,
    isTrackingHistoryEnabled,
    trackHistorys: trackHistorys || new Map(),
    isHighlightPluginEnabled,
  });

  useEffect(() => {
    setShowMenuBar(elementId === selectedElementId);
  }, [selectedElementId, elementId]);

  useEffect(() => {
    console.log("in useeffect..");
    const editor = rteRef?.current?.editor;
    console.log(editor);
    if (editor) {
      if (isTrackingEnabled) {
        editor.extensionManager.extensions.find(
          (ext) => ext.name === "trackChanges"
        ).options.isTrackingEnabled = true;
      } else {
        editor.extensionManager.extensions.find(
          (ext) => ext.name === "trackChanges"
        ).options.isTrackingEnabled = false;
      }
    }
  }, [isTrackingEnabled]);

  useEffect(() => {
    console.log("Updating isTrackingHistoryEnabled...");
    const editor = rteRef?.current?.editor;
    console.log(editor);

    if (editor) {
      const trackChangesExtension = editor.extensionManager.extensions.find(
        (ext) => ext.name === "trackChanges"
      );

      if (trackChangesExtension) {
        trackChangesExtension.options.isTrackingHistoryEnabled =
          isTrackingHistoryEnabled;
      }
      setIsTrackingEnabled(isTrackingHistoryEnabled);
    }

    console.log("Reflecting changes for isTrackingHistoryEnabled...");

    if (rteRef?.current?.editor?.commandManager?.commands?.setContent) {
      rteRef.current.editor.commandManager.commands.setContent(
        processParaEnableDisableTrackHistory(text, isTrackingHistoryEnabled)
      );
    }
  }, [isTrackingHistoryEnabled]);

  useEffect(() => {
    if (rteRef?.current?.editor?.commandManager?.commands?.setContent) {
      rteRef.current.editor.commandManager.commands.setContent(text);
    }
  }, [text, elementId]);

  // useEffect(() => {
  //   if (onTextChange) {
  //     const comments = rteRef?.current?.editor?.storage?.selectionHighlight?.comments || new Map();
  //     onTextChange(submittedContent, [...comments]);
  //   }
  // }, [submittedContent]);

  // const handleContentChange = useCallback(() => {
  //   if (onTextChange) {
  //     const content = rteRef.current?.editor?.getHTML() ?? "";
  //     const comments =
  //       rteRef?.current?.editor?.storage?.selectionHighlight?.comments || new Map();
  //     onTextChange(content, [...comments]);
  //   }
  // }, [onTextChange]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const nonEditorElements = document.querySelectorAll(".non-fullscreen");
      if (isFullscreen) {
        nonEditorElements.forEach((el) => el.classList.add("hidden"));
      } else {
        nonEditorElements.forEach((el) => el.classList.remove("hidden"));
      }
    };

    handleFullscreenChange(); // Apply styles on mount
    return () => {
      document
        .querySelectorAll(".hidden")
        .forEach((el) => el.classList.remove("hidden")); // Cleanup on unmount
    };
  }, [isFullscreen]);

  const handleNewImageFiles = useCallback((files, insertPosition) => {
    if (!rteRef.current?.editor) {
      return;
    }

    const attributesForImageFiles = files.map((file) => ({
      src: URL.createObjectURL(file),
      alt: file.name,
    }));

    insertImages({
      images: attributesForImageFiles,
      editor: rteRef.current.editor,
      insertPosition,
    });
  }, []);

  const handleDrop = useCallback(
    (view, event, _slice, _moved) => {
      if (!(event instanceof DragEvent) || !event.dataTransfer) {
        return false;
      }

      const imageFiles = fileListToImageFiles(event.dataTransfer.files);
      if (imageFiles.length > 0) {
        const insertPosition = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        })?.pos;

        handleNewImageFiles(imageFiles, insertPosition);

        event.preventDefault();
        return true;
      }

      return false;
    },
    [handleNewImageFiles]
  );

  const handlePaste = useCallback(
    (_view, event, _slice) => {
      if (!event.clipboardData) {
        return false;
      }

      const pastedImageFiles = fileListToImageFiles(event.clipboardData.files);
      if (pastedImageFiles.length > 0) {
        handleNewImageFiles(pastedImageFiles);
        return true;
      }

      return false;
    },
    [handleNewImageFiles]
  );

  const handleFullscreenToggle = () => {
    setIsFullscreen((prev) => !prev);
  };

  return (
    <>
      <Box
        ref={editorContainerRef}
        className={isFullscreen ? "editor-fullscreen" : "non-fullscreen"}
        sx={{
          backgroundColor: backgroundColor || "#ffffff",
          "& .ProseMirror": {
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              scrollMarginTop: showMenuBar ? { xs: 40, sm: 50, md: 60 } : 0,
            },
            ...(formPreview && {
              // Remove blue border from selected images only in preview mode
              "& img.ProseMirror-selectednode": {
                border: "none !important",
                outline: "none !important",
                boxShadow: "none !important",
              },
            }),
          },
          //width: formPreview ? "100%" : elementProps?.width,
          width: "100%",
          marginLeft: elementProps?.marginLeft,
          marginTop: elementProps?.marginTop,
          //height:elementProps?.height,
          Padding: elementProps?.Padding,
          ...(border && {
            maxHeight: "400px",
            overflow: "auto",
          }),
        }}
      >
        <RichTextEditor
          ref={rteRef}
          extensions={extensions}
          content={text}
          editable={isEditable}
          editorProps={{
            handleDrop: handleDrop,
            handlePaste: handlePaste,
          }}
          renderControls={(editor) => (
            <EditorMenuControls
              editor={editor}
              isTrackingEnabled={isTrackingEnabled}
              setIsTrackingEnabled={setIsTrackingEnabled}
              isTrackingHistoryEnabled={isTrackingHistoryEnabled}
              setIsTrackingHistoryEnabled={setIsTrackingHistoryEnabled}
            />
          )}
          RichTextFieldProps={{
            variant: border ? "outlined" : "standard", //outlined is for Bordered UI and standard is for without border and outlined
            MenuBarProps: {
              hide: !showMenuBar,
            },
            footer: showMenuBar ? (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  borderTopStyle: "solid",
                  borderTopWidth: 1,
                  borderTopColor: (theme) => theme.palette.divider,
                  py: { xs: 0.5, sm: 1, md: 1.5 },
                  px: { xs: 1, sm: 1.5, md: 2 },
                }}
              >
                <MenuButton
                  value="formatting"
                  tooltipLabel={
                    showMenuBar ? "Hide formatting" : "Show formatting"
                  }
                  size="small"
                  onClick={() =>
                    setShowMenuBar((currentState) => !currentState)
                  }
                  selected={showMenuBar}
                  IconComponent={TextFields}
                />

                <MenuButton
                  value="formatting"
                  tooltipLabel={
                    isEditable
                      ? "Prevent edits (use read-only mode)"
                      : "Allow edits"
                  }
                  size="small"
                  onClick={() => setIsEditable((currentState) => !currentState)}
                  selected={!isEditable}
                  IconComponent={isEditable ? Lock : LockOpen}
                />

                {/* <MenuButton
                  value="tracking"
                  tooltipLabel={isTrackingEnabled ? "Disable Tracking" : "Enable Tracking"}
                  size="small"
                  onClick={() => setIsTrackingEnabled((prev) => !prev)}
                  selected={isTrackingEnabled}
                  IconComponent={isTrackingEnabled ? Visibility : VisibilityOff}
                  sx={{
                    height: '28px',
                    minWidth: '28px',
                    padding: '4px',
                    backgroundColor: 'white',
                    borderColor: 'grey.300',
                    color: 'grey.600',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      borderColor: 'grey.400'
                    }
                  }}
                /> */}

                <Button
                  variant="contained"
                  size="small"
                  // onClick={() => {
                  //   setSubmittedContent(
                  //     rteRef.current?.editor?.getHTML() ?? "",
                  //   );
                  // }}
                  disabled={isTrackingHistoryEnabled}
                  onClick={() => handleSave(false)}
                >
                  Save
                </Button>
                <Tooltip
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  arrow
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleFullscreenToggle}
                  >
                    {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </Button>
                </Tooltip>
              </Stack>
            ) : null,
          }}
        >
          {() => (
            <>
              <LinkBubbleMenu />
              <TableBubbleMenu />
            </>
          )}
        </RichTextEditor>
      </Box>
      {isPresentMode && (
        <HighlightManager
          editor={rteRef.current?.editor}
          isPresentMode={isPresentMode}
        />
      )}
    </>
  );
}
